<template>
  <div>
    <div class="controls-container">


      <div class="control-group">
        <label>TXC Data:</label>
        <div class="button-row">
          <button @click="toggle('stops')" :class="{ toggled: toggles.stops }">Stops</button>
          <button @click="toggle('geofences')" :class="{ toggled: toggles.geofences }">Geo Fences</button>
          <button @click="toggle('stoproute')" :class="{ toggled: toggles.stoproute }">Stop Route</button>
          <button @click="toggle('tracking')" :class="{ toggled: toggles.tracking }">Tracking</button>
          <button @click="toggle('trackingsuppressed')"
            :class="{ toggled: toggles.trackingsuppressed }">Suppressed</button>
        </div>
      </div>

      <div class="control-group">
        <label>Bus Data:</label>
        <div class="button-row">
          <button @click="toggle('gps')" :class="{ toggled: toggles.gps }">GPS Route</button>
          <button @click="dataStore.fitMapToContent()">Fit Map</button>
        </div>
      </div>

      <div class="control-group full-width">
        <label>Journey Updates:</label>
        <div class="button-row">
          <span class="count-box">{{ journeyHistory ? journeyHistory.length : 0 }}</span>
          <select v-model="visualizedEvent" class="map-select" style="width: auto; flex-grow: 2;">
            <option v-for="evt in journeyHistory" :key="evt.evtidx || evt.time" :value="evt">
              {{ formateDate(evt.time) }} (Idx: {{ evt.evtidx }})
            </option>
          </select>
          <button @click="showHistory = true" style="flex-grow: 0; min-width: auto; padding: 0 15px;">History</button>
        </div>
      </div>
    </div>
    <div v-if="dataStore.timeFilters[props.jguid]"
      style="padding: 10px 20px; width: 100%; box-sizing: border-box; display: flex; align-items: center;">
      <input type="checkbox" v-model="timeFilterEnabled" style="margin-right: 15px; transform: scale(1.5);" />
      <div style="flex-grow: 1;">
        <TimeWindowSlider :min="minTime" :max="maxTime" v-model="timeWindow" />
      </div>
    </div>
    <div v-if="showHistory" class="data-modal">
      <div class="modal-content history-modal">
        <div class="modal-header">
          <h3>Journey History</h3>
          <button @click="showHistory = false" class="close-button">Close</button>
        </div>

        <div class="history-container">
          <!-- Sidebar: Event List -->
          <div class="history-sidebar">
            <div v-for="(evt, idx) in journeyHistory" :key="evt.evtidx || idx" class="history-item"
              :class="{ selected: isSelected(evt), visualized: visualizedEvent === evt }" @click="toggleSelection(evt)">
              <div class="history-item-header">
                <strong>{{ formateDate(evt.time) }}</strong>
                <button class="map-btn" @click.stop="visualizeOnMap(evt)" v-if="visualizedEvent !== evt">Map</button>
                <span v-else class="map-badge">Showing</span>
              </div>
              <div class="history-item-detail">
                Idx: {{ evt.evtidx }}
              </div>
            </div>
            <div v-if="journeyHistory.length === 0" class="no-history">
              No history events found.
            </div>
          </div>

          <!-- Main Content: View/Compare -->
          <div class="history-main">
            <div v-if="selectedEvents.length === 0" class="placeholder-text">
              Select an event to view details. Select two to compare.
            </div>

            <!-- Single View -->
            <div v-if="selectedEvents.length === 1" class="single-view full-height">
              <h4>Event Details ({{ formateDate(selectedEvents[0].time) }})</h4>
              <div class="diff-viewer-wrapper" style="overflow-y: auto;">
                <JsonTreeView :data="selectedEvents[0].evtdata" rootName="Event Data" />
              </div>
            </div>

            <!-- Compare View -->
            <div v-if="selectedEvents.length === 2" class="compare-view">
              <div class="compare-pane full-width">
                <div class="diff-header">
                  <h4>Comparison (Old vs New)</h4>
                  <div class="diff-controls">
                    <button @click="prevChange" :disabled="currentChangeIndex <= 0">Prev</button>
                    <span v-if="totalChanges > 0" class="diff-counter">{{ currentChangeIndex + 1 }} / {{ totalChanges
                    }}</span>
                    <span v-else class="diff-counter">0 / 0</span>
                    <button @click="nextChange" :disabled="currentChangeIndex >= totalChanges - 1">Next</button>
                  </div>
                </div>
                <div class="diff-viewer-wrapper">
                  <div class="diff-scroller" ref="diffContainerRef">
                    <div class="diff-content" v-html="diffHtml" @click="handleDiffClick"></div>
                  </div>
                  <div class="diff-minimap" @click="handleMinimapClick" v-if="minimapMarks.length > 0">
                    <div v-for="(mark, i) in minimapMarks" :key="i" class="minimap-mark" :class="mark.type"
                      :style="{ top: mark.top + '%', height: mark.height + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, reactive, onBeforeUnmount, ref, computed, watch, nextTick } from 'vue';
import { createCheckeredCircleIcon, createNumberedCircleIcon } from '../utilities/shapes.js';

import * as jsondiffpatch from 'jsondiffpatch';
import * as htmlFormatter from 'jsondiffpatch/formatters/html';

import JsonTreeView from './JsonTreeView.vue';
import TimeWindowSlider from './TimeWindowSlider.vue'; // Add Import

const dataStore = inject('datastore');

const props = defineProps({
  jguid: {
    type: String,
    required: true
  }
});

const toggles = reactive({});
let layers = {};

const isRefreshing = ref(false); // Used to suppress auto-fit
const showHistory = ref(false);
const selectedEvents = ref([]);

const journeyHistory = computed(() => {
  const events = dataStore.pidjourneyevents[props.jguid] || [];
  return events.filter(e => e.event === 'newjourney').sort((a, b) => {
    return (a.time - b.time) || ((a.evtidx || 0) - (b.evtidx || 0));
  });
});

const visualizedEvent = ref(null);

watch(journeyHistory, (newVal) => {
  if (newVal && newVal.length > 0 && !visualizedEvent.value) {
    visualizedEvent.value = newVal[0];
  }
}, { immediate: true });

const activeJourneyData = computed(() => {
  if (visualizedEvent.value) {
    return visualizedEvent.value;
  }
  return null;
});


// Time Filter Logic
const timeWindow = computed({
  get: () => {
    let f = dataStore.timeFilters[props.jguid];
    if (!f) return [0, 0];
    return [f.start, f.end];
  },
  set: (val) => {
    // console.log(`[TimeWindow] Set: ${val}`);
    if (dataStore.timeFilters[props.jguid]) {
      dataStore.timeFilters[props.jguid].start = val[0];
      dataStore.timeFilters[props.jguid].end = val[1];
    }
  }
});

// ...

// Watch time filter to refresh tracking layers
watch(() => dataStore.timeFilters[props.jguid], (newFilter) => {
  // console.log(`[Watcher] Time filter changed for ${props.jguid}`, newFilter);
  if (newFilter) {
    isRefreshing.value = true;
    if (toggles['tracking']) {
      hideLayer('tracking');
      showLayer('tracking');
    }
    if (toggles['trackingsuppressed']) {
      hideLayer('trackingsuppressed');
      showLayer('trackingsuppressed');
    }
    // Also GPS points if applicable
    if (toggles['gps']) {
      hideLayer('gps');
      showLayer('gps');
    }
    isRefreshing.value = false;
  }
}, { deep: true });


const timeFilterEnabled = computed({
  get: () => dataStore.timeFilters[props.jguid]?.enabled || false,
  set: (val) => {
    if (!dataStore.timeFilters[props.jguid]) {
      initTimeFilter();
    }
    if (dataStore.timeFilters[props.jguid]) {
      dataStore.timeFilters[props.jguid].enabled = val;
    }
  }
});

const minTime = computed(() => dataStore.timeFilters[props.jguid]?.min || 0);
const maxTime = computed(() => dataStore.timeFilters[props.jguid]?.max || 100);

function initTimeFilter() {
  // Use ALL events for this journey to find the full time range
  const allEvents = dataStore.pidjourneyevents[props.jguid];
  if (!allEvents || allEvents.length === 0) {
    console.log(`[TimeFilter] No events found for ${props.jguid}`);
    return;
  }

  console.log(`[TimeFilter] Scanning ${allEvents.length} events for time range...`);

  let min = Infinity;
  let max = -Infinity;

  allEvents.forEach(evt => {
    // Use event timestamp (usually in seconds based on previous code usage, e.g. formateDate)
    const t = Number(evt.time);
    if (!isNaN(t)) {
      if (t < min) min = t;
      if (t > max) max = t;
    }
  });

  if (min === Infinity) {
    console.warn(`[TimeFilter] Could not find any valid timestamps in event history. Defaulting to 0-100.`);
    min = 0; max = 100;
  } else {
    console.log(`[TimeFilter] Range found from history: ${min} - ${max}`);
  }

  // Set the filter
  const currentFilter = dataStore.timeFilters[props.jguid];
  dataStore.timeFilters[props.jguid] = {
    min: min,
    max: max,
    // Preserve existing selection if within new range, or reset to full range
    start: (currentFilter && currentFilter.start >= min) ? currentFilter.start : min,
    end: (currentFilter && currentFilter.end <= max) ? currentFilter.end : max,
    enabled: currentFilter ? currentFilter.enabled : false
  };
}

// Watch activeJourneyData to init filter once loaded
watch(activeJourneyData, (newData) => {
  if (newData && !dataStore.timeFilters[props.jguid]) {
    initTimeFilter();
  }
}, { immediate: true });

// Watch visualizedEvent to update map layers (auto-fit logic)
// Watch visualizedEvent to update map layers (auto-fit logic)


watch(visualizedEvent, async (newEvt) => {
  if (!newEvt) return;

  isRefreshing.value = true;
  await nextTick();

  // Draw layers
  drawJourney(newEvt.evtdata);

  // Auto-fit logic handled in drawJourney usually or here.
  // We want to autofit only if it's the first load OR user hasn't moved map properly?
  // Actually the requirement was "autofit map".
  isRefreshing.value = false;
});

function drawJourney(data) {
  hideAll();
  for (const key in toggles) {
    if (toggles[key]) {
      showLayer(key);
    }
  }
}



const differ = jsondiffpatch.create({});

const diffHtml = computed(() => {
  if (selectedEvents.value.length !== 2) return '';
  const left = selectedEvents.value[0].evtdata;
  const right = selectedEvents.value[1].evtdata;
  const delta = differ.diff(left, right);
  if (!delta) return 'No differences found.';
  return htmlFormatter.format(delta, left);
});

// Diff Navigation & Interaction
const diffContainerRef = ref(null);
const currentChangeIndex = ref(-1);
const totalChanges = ref(0);
const minimapMarks = ref([]);
let changeElements = [];

watch(diffHtml, async () => {
  await nextTick();
  if (diffContainerRef.value) {
    injectArrows(diffContainerRef.value);
  }
  updateDiffAnalysis();
});

function injectArrows(container) {
  if (!container) return;

  // Target ALL property names first
  const propNames = container.querySelectorAll('.jsondiffpatch-property-name');

  propNames.forEach(propName => {
    // Prevent duplicates
    if (propName.querySelector('.jsondiffpatch-arrow')) return;

    // Check if this property holds an object or array (collapsible)
    // The next sibling is usually the value.
    const valueDiv = propName.nextElementSibling;
    let isCollapsible = false;
    let typeClass = '';

    if (valueDiv && valueDiv.classList.contains('jsondiffpatch-value')) {
      const parent = propName.parentElement;
      if (parent.classList.contains('jsondiffpatch-child-node-type-object')) {
        isCollapsible = true;
      } else if (parent.classList.contains('jsondiffpatch-child-node-type-array')) {
        isCollapsible = true;
      } else {
        // Fallback: check content, and ADD class if missing so click handler works
        const text = valueDiv.textContent.trim();
        if (text.startsWith('{')) {
          isCollapsible = true;
          typeClass = 'jsondiffpatch-child-node-type-object';
        } else if (text.startsWith('[')) {
          isCollapsible = true;
          typeClass = 'jsondiffpatch-child-node-type-array';
        }
      }

      if (isCollapsible) {
        // Add missing class if needed
        if (typeClass && !parent.classList.contains(typeClass)) {
          parent.classList.add(typeClass);
        }

        const arrow = document.createElement('span');
        arrow.className = 'jsondiffpatch-arrow';
        propName.insertBefore(arrow, propName.firstChild);
      }
    }
  });
}

function updateDiffAnalysis() {
  if (!diffContainerRef.value) return;

  // Find all significant changes
  // Select added, deleted, modified nodes. 
  // jsondiffpatch classes: .jsondiffpatch-added, .jsondiffpatch-deleted, .jsondiffpatch-modified, .jsondiffpatch-textdiff-added, etc.
  // We target the wrapper containing the value.
  const container = diffContainerRef.value.querySelector('.diff-content');
  if (!container) return;

  const selector = '.jsondiffpatch-added, .jsondiffpatch-deleted, .jsondiffpatch-modified';
  changeElements = Array.from(container.querySelectorAll(selector));

  // Filter out nested ones if we want to jump to top-level changes only? 
  // For now, jump to all detected diff nodes.

  totalChanges.value = changeElements.length;
  currentChangeIndex.value = -1;

  // Build Minimap
  // We need relative positions.
  // Note: scrollHeight might change if things are collapsed, so this needs re-calc on expand/collapse often.
  // simpler: recalculate minimap on interaction or just initially.
  calculateMinimap();
}

function calculateMinimap() {
  if (!diffContainerRef.value || changeElements.length === 0) {
    minimapMarks.value = [];
    return;
  }

  const container = diffContainerRef.value; // The scrollable area
  const scrollHeight = container.scrollHeight;
  const containerRect = container.getBoundingClientRect();

  // The diff-content might be taller than container. 
  // We map position relative to scrollHeight.

  minimapMarks.value = changeElements.map(el => {
    // We need relative top inside the container
    // offsetTop is relative to offsetParent. 
    let top = el.offsetTop;
    let height = el.offsetHeight;

    // simple percentage
    let topPct = (top / scrollHeight) * 100;
    let heightPct = (height / scrollHeight) * 100;

    // Determine type
    let type = 'modified';
    if (el.classList.contains('jsondiffpatch-added')) type = 'added';
    if (el.classList.contains('jsondiffpatch-deleted')) type = 'deleted';

    return { top: topPct, height: Math.max(heightPct, 0.5), type }; // min height for visibility
  });
}

function nextChange() {
  if (totalChanges.value === 0) return;
  if (currentChangeIndex.value < totalChanges.value - 1) {
    currentChangeIndex.value++;
    scrollToChange(currentChangeIndex.value);
  }
}

function prevChange() {
  if (totalChanges.value === 0) return;
  if (currentChangeIndex.value > 0) {
    currentChangeIndex.value--;
    scrollToChange(currentChangeIndex.value);
  }
}

function scrollToChange(index) {
  const el = changeElements[index];
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Optionally flash it?
  }
}

function handleDiffClick(e) {
  let target = e.target;

  // Try to find the collapsible container (li or div)
  const container = target.closest('.jsondiffpatch-child-node-type-object, .jsondiffpatch-child-node-type-array');

  if (container) {
    const clickedHeader = target.closest('.jsondiffpatch-property-name') || target.classList.contains('jsondiffpatch-arrow');

    if (clickedHeader) {
      toggleNode(container);
      e.stopPropagation();
    }
  }
}

function toggleNode(node) {
  if (!node) return;

  const isCollapsed = node.classList.toggle('collapsed');

  // Force inline style to override any !important clash
  // We hide everything that isn't the header (property name)
  const children = Array.from(node.children);

  children.forEach(child => {
    // Skip the header row/container
    if (child.classList.contains('jsondiffpatch-property-name')) {
      return;
    }
    if (child.classList.contains('jsondiffpatch-arrow')) return; // Just in case it's direct child

    // Hide/Show
    if (isCollapsed) {
      child.style.setProperty('display', 'none', 'important');
    } else {
      child.style.removeProperty('display');
    }
  });

  // Trigger minimap update since positions changed
  setTimeout(updateDiffAnalysis, 300);
}


function handleMinimapClick(e) {
  // Scroll to percentage
  const rect = e.currentTarget.getBoundingClientRect();
  const y = e.clientY - rect.top;
  const pct = y / rect.height;

  if (diffContainerRef.value) {
    diffContainerRef.value.scrollTop = pct * diffContainerRef.value.scrollHeight;
  }
}


function isSelected(evt) {
  return selectedEvents.value.includes(evt);
}

function toggleSelection(evt) {
  const idx = selectedEvents.value.indexOf(evt);
  if (idx >= 0) {
    selectedEvents.value.splice(idx, 1);
  } else {
    if (selectedEvents.value.length >= 2) {
      // If 2 already selected, replace the second one (simulate a rolling selection or just limit)
      // Let's replace the older selection to keep it dynamic
      selectedEvents.value.shift();
    }
    selectedEvents.value.push(evt);
  }
}
let layerBuilders = {};

onBeforeUnmount(() => {
  hideAll()
});

function hideAll() {
  for (let name in layers) {
    hideLayer(name);
  }

}
function hideLayer(name) {
  if (layers[name]) {
    dataStore.removeLayer(layers[name]);
    delete layers[name]; // Force rebuild next time showLayer is called
  }
}

function showLayer(name) {
  if (!layers[name]) {
    if (layerBuilders[name]) {
      layers[name] = layerBuilders[name]();
    }
  }

  if (layers[name]) {
    // Check if map is currently empty
    const wasEmpty = Object.keys(dataStore.activeLayers).length === 0;

    dataStore.addLayer(layers[name]);

    // Auto-fit only if it was empty AND not refreshing content
    // console.log(`[ShowLayer:${name}] Empty: ${wasEmpty}, Refreshing: ${isRefreshing.value}`);
    if (wasEmpty && !isRefreshing.value) {
      console.log(`[${props.jguid}]Auto - fitting map(was empty)`);
      dataStore.fitMapToContent();
    }
  }
}

function toggle(what) {
  toggles[what] = !toggles[what];
  if (!toggles[what]) {
    hideLayer(what);
  } else {
    showLayer(what);
  }
}

// Local fitMapToContent removed. Using dataStore.fitMapToContent()

function layerbuilder(name, fn) {
  layerBuilders[name] = fn;
}



//------------------------------------------
// Stops
function stopPopupText(idx, stop) {
  let str =
    `
      <b>Stop number</b>: ${idx}<br />
        <b>Fullname</b>: ${stop.fullname}<br />
          <b>Reference</b>: ${stop.reference}<br />
            <b>Lat</b>: ${stop.lat}<br />
              <b>Lon</b>: ${(stop.lon)}<br /> `

  return str
}


layerbuilder('stops', () => {
  let layer = L.layerGroup();

  let eventCol = dataStore.eventColours['stops'] || 0x000000

  let journeyEvent = activeJourneyData.value;
  let stops = journeyEvent?.evtdata?.stops

  if (stops) {

    // add markers for the events
    const timeFilter = dataStore.timeFilters[props.jguid];

    for (let [index, stop] of stops.entries()) {

      let size = 24
      let isLastStop = index + 1 == stops.length
      let isCancelled = stop.isCancelled;

      if (index == 0 || isLastStop) {
        size = size * 1.5
      }

      if (stop?.lat && stop?.lon) {

        let circleIconUrl
        if (isLastStop) {
          circleIconUrl = createCheckeredCircleIcon({
            size: 24,
            checksize: 6,
            color1: "black",
            color2: "white",
            stroke: eventCol,
            strokewidth: 4,
            number: index + 1
          });

        } else {
          // Normal stop
          let stopColor = eventCol;
          let fontColor = "black";
          let strokeColor = "black";

          if (isCancelled) {
            stopColor = "#cccccc"; // Grey out
            fontColor = "#777777";
            strokeColor = "red"; // Red border to indicate cancellation
          }

          circleIconUrl = createNumberedCircleIcon({
            size: 24,
            checksize: 6,
            color: stopColor,
            fontcolor: fontColor,
            stroke: strokeColor,
            strokewidth: isCancelled ? 3 : 4,
            number: index + 1
          });

        }

        const circleIcon = L.divIcon({
          className: 'checkered-circle',
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
          html: `<img src="${circleIconUrl}" width="${size}" height="${size}" style="border-radius: 50%;" />`
        });



        let popupText = stopPopupText(index + 1, stop);
        if (isCancelled) {
          popupText = `<b style="color:red;">(CANCELLED)</b><br />` + popupText;
        }

        const marker = L.marker([stop.lat, stop.lon], { icon: circleIcon }).bindTooltip(popupText, { sticky: false });


        marker.addTo(layer);
      }
    }


  }

  return layer;
});

layerbuilder('geofences', () => {
  let layer = L.layerGroup();
  let journeyEvent = activeJourneyData.value;
  let stops = journeyEvent?.evtdata?.stops;

  if (stops) {
    for (let stop of stops) {
      if (stop?.lat && stop?.lon) {
        // Outer Geofence
        if (stop.outerGeoFence) {
          L.circle([stop.lat, stop.lon], {
            radius: stop.outerGeoFence * 1000,
            color: '#ff7800',
            fillColor: '#ff7800',
            fillOpacity: 0.2,
            weight: 1,
            interactive: false
          }).addTo(layer);
        }
        // Inner Geofence
        if (stop.innerGeoFence) {
          L.circle([stop.lat, stop.lon], {
            radius: stop.innerGeoFence * 1000,
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.3,
            weight: 1,
            interactive: false
          }).addTo(layer);
        }
      }
    }
  }

  // Ensure geofences are in the background
  layer.on('add', () => {
    layer.eachLayer(l => {
      if (l.bringToBack) l.bringToBack();
    });
  });

  return layer;
});

layerbuilder('stoproute', () => {
  let layer = L.layerGroup();

  let eventCol = dataStore.eventColours['stops'] || 0x000000

  let journeyEvent = activeJourneyData.value;
  let stops = journeyEvent?.evtdata?.stops


  if (stops) {

    // add markers for the events

    let gpsCoords = [];
    for (let [index, stop] of stops.entries()) {
      if (stop?.lat && stop?.lon) {
        gpsCoords.push([stop.lat, stop.lon]);
      }
    }

    const polyline = L.polyline(gpsCoords, { color: eventCol }).addTo(layer);

    L.polylineDecorator(polyline, {
      patterns: [
        {
          repeat: '50px',
          symbol: L.Symbol.arrowHead({
            pixelSize: 10,
            polygon: false,
            pathOptions: { stroke: true, color: 'red' } // Customize the arrow's appearance
          })
        }
      ]
    }).addTo(layer);

  }

  return layer;

});

function formateDate(time) {
  time *= 1000
  const date = new Date(time);
  return date.toLocaleString(undefined, {
    weekday: 'long',  // e.g., "Friday"
    year: 'numeric',  // e.g., "2024"
    month: 'long',    // e.g., "November"
    day: 'numeric',   // e.g., "8"
    hour: '2-digit',  // e.g., "11" (for "11 AM")
    minute: '2-digit', // e.g., "30"
    second: '2-digit', // e.g., "30"
  });
}

function popupbuilder(fromstopref, tostopref, route, point) {
  return `
      < b > From stop</b >: ${fromstopref}</br >
        <b>To stop</b>: ${tostopref}</br >
          <b>Dist</b>: ${point.dist}</br >
            <b>Lat</b>: ${point.lat}</br >
              <b>Lon</b>: ${(point.lon)}</br > `

}

function buildTrackingLayer(showSuppressedOnly) {
  let layer = L.layerGroup();

  let eventCol = dataStore.eventColours['tracking'] || 0x000000
  let eventColSupp = dataStore.eventColours['trackingSuppress'] || 0xff0000

  let journeyEvent = activeJourneyData.value;
  let interstoproutes = journeyEvent?.evtdata?.interstoproutes || journeyEvent?.evtdata?.interStopRoutes
  //let interstoproutes = journeyEvent?.evtdata?.journeyRoutes

  if (!interstoproutes) {
    console.log("No interstop routes")
  } else {

    // console.log(`interstop routes ${ JSON.stringify(interstoproutes) } `)

    for (let fromstopref in interstoproutes) {
      // console.log(`fromstopref[${ fromstopref }]`)

      let fromstop = interstoproutes[fromstopref]
      if (fromstop.to) {
        fromstop = fromstop.to
      }

      for (let tostopref in fromstop) {

        // console.log(`tostopref[${ tostopref }]`)
        let tostop = fromstop[tostopref]
        // console.log(`tostop[${ JSON.stringify(tostop) }]`)

        let routes = tostop.routes

        for (let routeidx in routes) {
          // console.log(`routeidx[${ routeidx }]`)
          let useCol = eventCol
          let isSuppressed = !!routes[routeidx].suppress

          if (isSuppressed) {
            useCol = eventColSupp
          }

          if (isSuppressed !== showSuppressedOnly) {
            continue;
          }

          let points = routes[routeidx].points

          let gpsCoords = [];

          const timeFilter = dataStore.timeFilters[props.jguid];
          if (timeFilter && timeFilter.enabled) {
            console.log(`[Tracking] Filtering enabled. Range: ${timeFilter.start} - ${timeFilter.end}`);
          }

          for (let point of points) {
            // console.log(`point[${ point }]`)

            // Time Filter
            if (timeFilter && timeFilter.enabled && point.t) {
              if (point.t < timeFilter.start || point.t > timeFilter.end) {
                // console.log(`Filtered out point at ${point.t} (Range: ${timeFilter.start}-${timeFilter.end})`);
                continue;
              }
            }

            if (point?.lat && point?.lon) {
              gpsCoords.push([point.lat, point.lon]);

              const marker = L.circleMarker([point.lat, point.lon], {
                radius: 4,
                color: 'black',
                fillColor: useCol,
                fillOpacity: 1
              }).bindTooltip(popupbuilder(fromstopref, tostopref, routes[routeidx], point), { sticky: false });

              marker.addTo(layer);
            }

          }
          const polyline = L.polyline(gpsCoords, { color: useCol, weight: 3 }).addTo(layer); // Set default weight explicitly

          polyline.on('mouseover', function (e) {
            var layer = e.target;
            layer.setStyle({
              color: '#00FFFF', // Highlight color (Cyan)
              weight: 5
            });
            layer.bringToFront();
          });

          polyline.on('mouseout', function (e) {
            var layer = e.target;
            layer.setStyle({
              color: useCol,
              weight: 3
            });
          });

        }


      }
    }


  }

  return layer;
}

layerbuilder('tracking', () => {
  return buildTrackingLayer(false);
});

layerbuilder('trackingsuppressed', () => {
  return buildTrackingLayer(true);
});

//------------------------------------------
// Build GPS
function gpsPopupText(stop, num) {
  let str =
    `
      < b > Number</b >: ${num}</br >
        <b>Time</b>: ${stop.time}</br >
          <b>Time</b>: ${formateDate(stop.time)}</br >
            <b>Lat</b>: ${stop.gps?.lat}</br >
              <b>Lon</b>: ${(stop.gps?.lon)}</br > `

  return str
}

layerbuilder('gps', () => {
  let layer = L.layerGroup();

  let eventCol = dataStore.eventColours['route'] || 0x000000

  let journeyEvents = dataStore.pidjourneyevents[props.jguid];
  if (journeyEvents) {

    let gpsCoords = [];

    let evtNumber = 0
    // add markers for the events
    for (let evt of journeyEvents) {

      if (evt.guid !== props.jguid) {
        console.log("Mismatched guid")
        continue;
      }

      if (evt.event === "pos") {

        // Time Filter
        const timeFilter = dataStore.timeFilters[props.jguid];
        if (timeFilter && timeFilter.enabled) {
          const t = Number(evt.time);
          if (t < timeFilter.start || t > timeFilter.end) {
            continue;
          }
        }

        evtNumber++;
        let gps = evt.gps
        if (gps?.lat && gps?.lon) {

          const marker = L.circleMarker([gps.lat, gps.lon], {
            radius: 6,
            color: 'black',
            fillColor: eventCol,
            fillOpacity: 1
          }).bindTooltip(gpsPopupText(evt, evtNumber), { sticky: false });

          marker.addTo(layer);
        }

        if (evt?.gps?.lat && evt?.gps?.lon) {
          gpsCoords.push([evt.gps.lat, evt.gps.lon]);
        }
      }

    }
    const polyline = L.polyline(gpsCoords, { color: eventCol }).addTo(layer);
    L.polylineDecorator(polyline, {
      patterns: [
        {
          repeat: '50px',
          symbol: L.Symbol.arrowHead({
            pixelSize: 10,
            polygon: false,
            pathOptions: { stroke: true, color: 'red' } // Customize the arrow's appearance
          })
        }
      ]
    }).addTo(layer);

  }

  return layer;

});

// Watch time filter to refresh tracking layers
watch(() => dataStore.timeFilters[props.jguid], (newFilter) => {
  // console.log(`[Watcher] Time filter changed for ${props.jguid}`, newFilter);
  if (newFilter) {
    isRefreshing.value = true;
    if (toggles['tracking']) {
      hideLayer('tracking');
      showLayer('tracking');
    }
    if (toggles['trackingsuppressed']) {
      hideLayer('trackingsuppressed');
      showLayer('trackingsuppressed');
    }
    // Also GPS points if applicable
    if (toggles['gps']) {
      hideLayer('gps');
      showLayer('gps');
    }
    isRefreshing.value = false;
  }
  // Forced rebuild trigger
}, { deep: true });

</script>

<style>
.event-count-table {
  width: 100%;
  /* Make the table take up 100% of the container width */
  table-layout: auto;
  /* Fix the layout of the table */
  border-collapse: collapse;
}

.event-count-table thead {
  background-color: #007bff;
  /* Set the background color of the header row */
  color: white;
  /* Optional: change the text color to contrast */
}

.event-count-table th,
.event-count-table td {
  border: 1px solid #ccc;
  padding: 0px 10px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
}

.event-count-table th:nth-child(4),
/* Target the third column for the extra space */
.event-count-table td:nth-child(4) {
  width: 100%;
  /* Let this column take all the remaining space */
}
</style>

<style scoped>
.count-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  background-color: #eee;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-right: 5px;
  font-weight: bold;
  min-width: 30px;
}

.selected {
  background-color: #f0f0f0;
  /* Highlight color */
}

/* Style for when the button is toggled on */
.toggled {
  background-color: green;
  color: white;
}

/* Default button style */
button {
  background-color: gray;
  color: black;
}

/* Modal Styles */
.data-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  /* High z-index */
}

.history-modal {
  width: 90%;
  height: 90%;
  background: white;
  display: flex;
  flex-direction: column;
  padding: 0;
  /* Override default padding for layout */
}

.modal-header {
  padding: 10px 20px;
  background: #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
}

.history-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.history-sidebar {
  width: 250px;
  border-right: 1px solid #ccc;
  overflow-y: auto;
  background: #f9f9f9;
}

.history-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.history-item:hover {
  background-color: #eef;
}

.history-item.selected {
  background-color: #cce5ff;
  border-left: 4px solid #007bff;
}

.history-main {
  flex: 1;
  overflow: hidden;
  /* Manage scroll internally if needed */
  display: flex;
  flex-direction: column;
  padding: 10px;
}

.placeholder-text {
  padding: 20px;
  color: #888;
  text-align: center;
}

.single-view {
  flex: 1;
  overflow-y: auto;
  background: #fff;
  padding: 10px;
  border: 1px solid #ddd;
}

.map-btn {
  font-size: 10px;
  padding: 2px 5px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 3px;
  margin-left: 5px;
  cursor: pointer;
}

.map-badge {
  font-size: 10px;
  padding: 2px 5px;
  background: #28a745;
  color: white;
  border-radius: 3px;
  margin-left: 5px;
}

.history-item.visualized {
  background-color: #e6fffa;
  /* Light teal to indicate it's on map */
}



.compare-view {
  display: flex;
  flex: 1;
  gap: 10px;
  overflow: hidden;
}

.compare-pane {
  flex: 1;
  overflow: hidden;
  /* Changed from auto to hidden to allow internal container to scroll */
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 0;
  /* Remove padding */
  border: 1px solid #ddd;
}
</style>

<style>
/* jsondiffpatch Styles */
.jsondiffpatch-delta {
  font-family: "Bitstream Vera Sans Mono", "DejaVu Sans Mono", Monaco, Courier, monospace;
  font-size: 12px;
  margin: 0;
  padding: 0 0 0 12px;
  display: inline-block;
}

.jsondiffpatch-delta pre {
  font-family: "Bitstream Vera Sans Mono", "DejaVu Sans Mono", Monaco, Courier, monospace;
  font-size: 12px;
  margin: 0;
  padding: 0;
  display: inline-block;
}

ul.jsondiffpatch-delta {
  list-style-type: none;
  padding: 0 0 0 20px;
  margin: 0;
}

.jsondiffpatch-delta ul {
  list-style-type: none;
  padding: 0 0 0 20px;
  margin: 0;
}

.jsondiffpatch-added .jsondiffpatch-property-name,
.jsondiffpatch-added .jsondiffpatch-value pre,
.jsondiffpatch-modified .jsondiffpatch-right-value pre,
.jsondiffpatch-textdiff-added {
  background: #bbffbb;
}

.jsondiffpatch-deleted .jsondiffpatch-property-name,
.jsondiffpatch-deleted pre,
.jsondiffpatch-modified .jsondiffpatch-left-value pre,
.jsondiffpatch-textdiff-deleted {
  background: #ffbbbb;
  text-decoration: line-through;
}

.jsondiffpatch-unchanged,
.jsondiffpatch-movedestination {
  color: gray;
}

.jsondiffpatch-unchanged,
.jsondiffpatch-movedestination>.jsondiffpatch-value {
  transition: all 0.5s;
  -webkit-transition: all 0.5s;
  overflow-y: hidden;
}

.jsondiffpatch-unchanged-showing .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-showing .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 100px;
}

.jsondiffpatch-unchanged-hidden .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 0;
}

.jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination>.jsondiffpatch-value,
.jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination>.jsondiffpatch-value {
  display: block;
}

.jsondiffpatch-unchanged-visible .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-visible .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 100px;
}

.jsondiffpatch-unchanged-hiding .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 0;
}

.jsondiffpatch-unchanged-showing .jsondiffpatch-arrow,
.jsondiffpatch-unchanged-hiding .jsondiffpatch-arrow {
  display: none;
}

.jsondiffpatch-value {
  display: inline-block;
}

.jsondiffpatch-property-name {
  display: inline-block;
  padding-right: 5px;
  vertical-align: top;
}

.jsondiffpatch-property-name:after {
  content: ": ";
}

.jsondiffpatch-child-node-type-array>.jsondiffpatch-property-name:after {
  content: ": [";
}

.jsondiffpatch-child-node-type-array:after {
  content: "],";
}

div.jsondiffpatch-child-node-type-array:before {
  content: "[";
}

div.jsondiffpatch-child-node-type-array:after {
  content: "]";
}

.jsondiffpatch-child-node-type-object>.jsondiffpatch-property-name:after {
  content: ": {";
}

.jsondiffpatch-child-node-type-object:after {
  content: "},";
}

div.jsondiffpatch-child-node-type-object:before {
  content: "{";
}

div.jsondiffpatch-child-node-type-object:after {
  content: "}";
}

.jsondiffpatch-value pre:after {
  content: ",";
}

li:last-child>.jsondiffpatch-value pre:after,
.jsondiffpatch-modified>.jsondiffpatch-left-value pre:after {
  content: "";
}

.jsondiffpatch-modified .jsondiffpatch-value {
  display: inline-block;
}

.jsondiffpatch-modified .jsondiffpatch-right-value {
  margin-left: 5px;
}

.jsondiffpatch-moved .jsondiffpatch-property-name {
  text-decoration: line-through;
  text-decoration-color: gray;
}

.jsondiffpatch-moved .jsondiffpatch-value {
  display: none;
}

.jsondiffpatch-moved .jsondiffpatch-moved-destination {
  display: inline-block;
  background: #ffffbb;
  color: #888;
}

.jsondiffpatch-moved .jsondiffpatch-moved-destination:before {
  content: " => ";
}

ul.jsondiffpatch-textdiff {
  padding: 0;
}

.jsondiffpatch-textdiff-location {
  color: #bbb;
  display: inline-block;
  min-width: 60px;
}

.jsondiffpatch-textdiff-line {
  display: inline-block;
}

.jsondiffpatch-textdiff-line-number:after {
  content: ",";
}

.jsondiffpatch-error {
  background: red;
  color: white;
  font-weight: bold;
}

.jsondiffpatch-arrow {
  cursor: pointer;
  color: #ddd;
  font-family: Arial;
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;
  text-align: center;
  line-height: 11px;
  margin-right: 2px;
  margin-top: 1px;
  vertical-align: top;
}

.jsondiffpatch-arrow:before {
  content: "▼";
  position: absolute;
  top: 0;
  left: 0;
}

.jsondiffpatch-child-node-type-array.collapsed>.jsondiffpatch-property-name .jsondiffpatch-arrow:before,
.jsondiffpatch-child-node-type-object.collapsed>.jsondiffpatch-property-name .jsondiffpatch-arrow:before {
  content: "▶";
  position: absolute;
  top: 0;
  left: 0;
}

/* Custom Overrides */
/* Side-by-Side Modifications */
.jsondiffpatch-modified .jsondiffpatch-value {
  display: flex !important;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
}

.jsondiffpatch-modified .jsondiffpatch-left-value,
.jsondiffpatch-modified .jsondiffpatch-right-value {
  display: inline-block;
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-all;
  padding: 2px 4px;
  border-radius: 2px;
}

.jsondiffpatch-modified .jsondiffpatch-left-value {
  background-color: #ffe6e6;
  border: 1px solid #ffcccc;
}

.jsondiffpatch-modified .jsondiffpatch-right-value {
  background-color: #e6fffa;
  border: 1px solid #ccfffa;
  margin-left: 0;
}

/* Collapsing overrides */
.jsondiffpatch-child-node-type-object.collapsed>.jsondiffpatch-value,
.jsondiffpatch-child-node-type-array.collapsed>.jsondiffpatch-value {
  display: none !important;
}

.jsondiffpatch-child-node-type-object.collapsed:after {
  content: " { ... }";
  color: #888;
  display: inline-block;
}

.jsondiffpatch-child-node-type-array.collapsed:after {
  content: " [ ... ]";
  color: #888;
  display: inline-block;
}

/* New Diff UI Styles */
.jsondiffpatch-delta {

  font-family: "Bitstream Vera Sans Mono", "DejaVu Sans Mono", Monaco, Courier,
    monospace;
  font-size: 12px;
  margin: 0;
  padding: 0 0 0 12px;
  display: inline-block;
}

/* New Diff UI Styles */
.diff-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  background: #f0f0f0;
  border-bottom: 1px solid #ccc;
}

.diff-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

.diff-counter {
  font-family: monospace;
  font-weight: bold;
}

.diff-container {
  position: relative;
  height: 600px;
  /* Fixed height for scrolling */
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
}

.diff-minimap {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 100%;
  pointer-events: auto;
  /* Allow clicking */
  background: rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.minimap-mark {
  position: absolute;
  width: 100%;
  left: 0;
  opacity: 0.7;
}

.minimap-mark.added {
  background-color: #28a745;
}

.minimap-mark.deleted {
  background-color: #dc3545;
}

.minimap-mark.modified {
  background-color: #ffc107;
}

/* Collapsing overrides */
.jsondiffpatch-child-node-type-object.collapsed>.jsondiffpatch-value,
.jsondiffpatch-child-node-type-array.collapsed>.jsondiffpatch-value {
  display: none !important;
}

.jsondiffpatch-child-node-type-object.collapsed:after {
  content: " { ... }";
  color: #888;
  display: inline-block;
}

.jsondiffpatch-child-node-type-array.collapsed:after {
  content: " [ ... ]";
  color: #888;
  display: inline-block;
}

.jsondiffpatch-delta pre {
  font-family: "Bitstream Vera Sans Mono", "DejaVu Sans Mono", Monaco, Courier,
    monospace;
  font-size: 12px;
  margin: 0;
  padding: 0;
  display: inline-block;
}

ul.jsondiffpatch-delta {
  list-style-type: none;
  padding: 0 0 0 20px;
  margin: 0;
}

.jsondiffpatch-delta ul {
  list-style-type: none;
  padding: 0 0 0 20px;
  margin: 0;
}

.jsondiffpatch-added .jsondiffpatch-property-name,
.jsondiffpatch-added .jsondiffpatch-value pre,
.jsondiffpatch-modified .jsondiffpatch-right-value pre,
.jsondiffpatch-textdiff-added {
  background: #bbffbb;
}

.jsondiffpatch-deleted .jsondiffpatch-property-name,
.jsondiffpatch-deleted pre,
.jsondiffpatch-modified .jsondiffpatch-left-value pre,
.jsondiffpatch-textdiff-deleted {
  background: #ffbbbb;
  text-decoration: line-through;
}

.jsondiffpatch-unchanged,
.jsondiffpatch-movedestination {
  color: gray;
}

.jsondiffpatch-unchanged,
.jsondiffpatch-movedestination>.jsondiffpatch-value {
  transition: all 0.5s;
  -webkit-transition: all 0.5s;
  overflow-y: hidden;
}

.jsondiffpatch-unchanged-showing .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-showing .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 100px;
}

.jsondiffpatch-unchanged-hidden .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 0;
}

.jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination>.jsondiffpatch-value,
.jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination>.jsondiffpatch-value {
  display: block;
}

.jsondiffpatch-unchanged-visible .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-visible .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 100px;
}

.jsondiffpatch-unchanged-hiding .jsondiffpatch-unchanged,
.jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination>.jsondiffpatch-value {
  max-height: 0;
}

.jsondiffpatch-unchanged-showing .jsondiffpatch-arrow,
.jsondiffpatch-unchanged-hiding .jsondiffpatch-arrow {
  display: none;
}

.jsondiffpatch-value {
  display: inline-block;
}

.jsondiffpatch-property-name {
  display: inline-block;
  padding-right: 5px;
  vertical-align: top;
}

.jsondiffpatch-property-name:after {
  content: ": ";
}

.jsondiffpatch-child-node-type-array>.jsondiffpatch-property-name:after {
  content: ": [";
}

.jsondiffpatch-child-node-type-array:after {
  content: "],";
}

div.jsondiffpatch-child-node-type-array:before {
  content: "[";
}

div.jsondiffpatch-child-node-type-array:after {
  content: "]";
}

.jsondiffpatch-child-node-type-object>.jsondiffpatch-property-name:after {
  content: ": {";
}

.jsondiffpatch-child-node-type-object:after {
  content: "},";
}

div.jsondiffpatch-child-node-type-object:before {
  content: "{";
}

div.jsondiffpatch-child-node-type-object:after {
  content: "}";
}

.jsondiffpatch-value pre:after {
  content: ",";
}

li:last-child>.jsondiffpatch-value pre:after,
.jsondiffpatch-modified>.jsondiffpatch-left-value pre:after {
  content: "";
}

.jsondiffpatch-modified .jsondiffpatch-value {
  display: inline-block;
}

.jsondiffpatch-modified .jsondiffpatch-right-value {
  margin-left: 5px;
}

.jsondiffpatch-moved .jsondiffpatch-property-name {
  text-decoration: line-through;
  text-decoration-color: gray;
}

.jsondiffpatch-moved .jsondiffpatch-value {
  display: none;
}

.jsondiffpatch-moved .jsondiffpatch-moved-destination {
  display: inline-block;
  background: #ffffbb;
  color: #888;
}

.jsondiffpatch-moved .jsondiffpatch-moved-destination:before {
  content: " => ";
}

ul.jsondiffpatch-textdiff {
  padding: 0;
}

.jsondiffpatch-textdiff-location {
  color: #bbb;
  display: inline-block;
  min-width: 60px;
}

.jsondiffpatch-textdiff-line {
  display: inline-block;
}

.jsondiffpatch-textdiff-line-number:after {
  content: ",";
}

.jsondiffpatch-error {
  background: red;
  color: white;
  font-weight: bold;
}


/* New Diff UI Styles */
.diff-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  background: #f0f0f0;
  border-bottom: 1px solid #ccc;
}

.diff-controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

.diff-counter {
  font-family: monospace;
  font-weight: bold;
}

.diff-viewer-wrapper {
  position: relative;
  height: 600px;
  background: white;
}

.diff-scroller {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  /* Ensure offsetTop is relative to this */
}

.diff-minimap {
  position: absolute;
  top: 0;
  right: 20px;
  /* Moved left to avoid scrollbar overlap */
  width: 12px;
  height: 100%;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.minimap-mark {
  position: absolute;
  width: 100%;
  left: 0;
  opacity: 0.7;
}

.minimap-mark.added {
  background-color: #28a745;
}

.minimap-mark.deleted {
  background-color: #dc3545;
}

.minimap-mark.modified {
  background-color: #ffc107;
}

/* Collapsing overrides */
.jsondiffpatch-child-node-type-object.collapsed>.jsondiffpatch-value,
.jsondiffpatch-child-node-type-array.collapsed>.jsondiffpatch-value {
  display: none !important;
}

.jsondiffpatch-child-node-type-object.collapsed:after {
  content: " { ... }";
  color: #888;
  display: inline-block;
}

.jsondiffpatch-child-node-type-array.collapsed:after {
  content: " [ ... ]";
  color: #888;
  display: inline-block;
}

/* Panel Control Layout */
.controls-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-bottom: 2px solid #ddd;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.control-group label {
  font-size: 11px;
  font-weight: bold;
  color: #555;
  text-transform: uppercase;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.control-group.full-width {
  width: 100%;
}

.map-select {
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.controls-container button {
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 3px;
  flex-grow: 1;
  /* Allow buttons to expand */
  min-width: 60px;
  text-align: center;
}

.controls-container button.toggled {
  background: #4caf50;
  color: white;
  border-color: #388e3c;
}

.controls-container button:hover {
  background: #e0e0e0;
}

.controls-container button.toggled:hover {
  background: #45a049;
}

/* Side-by-Side Modifications */
.jsondiffpatch-modified .jsondiffpatch-value {
  display: flex !important;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
}

.jsondiffpatch-modified .jsondiffpatch-left-value,
.jsondiffpatch-modified .jsondiffpatch-right-value {
  display: inline-block;
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-all;
  padding: 2px 4px;
  border-radius: 2px;
}

.jsondiffpatch-modified .jsondiffpatch-left-value {
  background-color: #ffe6e6;
  border: 1px solid #ffcccc;
}

.jsondiffpatch-modified .jsondiffpatch-right-value {
  background-color: #e6fffa;
  border: 1px solid #ccfffa;
  margin-left: 0;
}

/* Arrow Styles */
.jsondiffpatch-arrow {
  display: inline-block;
  cursor: pointer;
  font-size: 10px;
  width: 20px;
  text-align: center;
  color: #888;
  user-select: none;
  transition: transform 0.2s;
  vertical-align: middle;
}

.jsondiffpatch-arrow:before {
  content: "▼";
}

.jsondiffpatch-child-node-type-object.collapsed>.jsondiffpatch-property-name .jsondiffpatch-arrow:before,
.jsondiffpatch-child-node-type-array.collapsed>.jsondiffpatch-property-name .jsondiffpatch-arrow:before {
  content: "▶";
}

/* Override previous display:none if it clashes */
.jsondiffpatch-arrow {
  display: inline-block !important;
  /* Force show */
}
</style>
